import React from "react";
import ReactDOM from "react-dom";
import { motion, AnimatePresence, useDragControls } from "framer-motion";
import { useTranslation } from "react-i18next";
import useStorePlayer from "store/useStorePlayer";
import useStoreUserPanel from "store/useStoreUserPanel";
import useStoreToast from "store/useStoreToast";
import ProfileTab from "./tabs/account/ProfileTab";
import CustomizeTab from "./tabs/account/CustomizeTab";
import ButtonHapticWrapper from "../Button/ButtonHapticWrapper";
import useThemeColor from "hook/useThemeColor";

type UserPanelProps = {
    isOpen: boolean;
    onClose: () => void;
};

const tabIcons: Record<string, React.ReactNode> = {
    profile: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
    ),
    customize: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
        </svg>
    ),
};

const UserPanel = ({ isOpen, onClose }: UserPanelProps) => {
    const { t } = useTranslation();
    const { activeAccountTab, setActiveAccountTab } = useStoreUserPanel();
    const dragControls = useDragControls();

    const handleLogout = () => {
        useStorePlayer.getState().logout();
        onClose();
        useStoreToast.getState().show(t("user.logout_success"), "success");
    };

    useThemeColor(isOpen ? "#091D3D" : null);

    const accountTabs = [
        { id: "profile" as const, label: t("user.tab_profile") },
        { id: "customize" as const, label: t("user.tab_customize") },
    ];

    return ReactDOM.createPortal(
        <motion.div
            initial={{ opacity: 0 }}
            animate={isOpen ? { opacity: 1 } : { opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[100]"
            style={{ pointerEvents: isOpen ? "auto" : "none" }}
        >
            <div
                className={`fixed inset-0 bg-black/70 z-[100] ${isOpen ? "" : "pointer-events-none"}`}
                onClick={onClose}
            />
            <div data-tooltip-container className="fixed inset-0 z-[101] pointer-events-none flex items-end md:items-center md:justify-center">
                <motion.div
                    initial={{ y: "100%" }}
                    animate={isOpen ? { y: 0 } : { y: "100%" }}
                    transition={{ type: "spring", damping: 30, stiffness: 300 }}
                    drag="y"
                    dragControls={dragControls}
                    dragListener={false}
                    dragConstraints={{ top: 0, bottom: 0 }}
                    dragElastic={{ top: 0, bottom: 0.5 }}
                    onDragEnd={(_e, info) => {
                        if(info.offset.y > 100 || info.velocity.y > 300) {
                            onClose();
                        }
                    }}
                    className={`${isOpen ? "pointer-events-auto" : "pointer-events-none"} relative w-full h-[80vh] xs:h-[70vh] md:max-w-md rounded-t-2xl md:rounded-2xl overflow-hidden border border-white/10 flex flex-col`}
                    style={{ background: "linear-gradient(180deg, #2570D9 0%, #1A5BC4 100%)" }}
                >

                            {/* Content */}
                            <div className="px-4 pb-8 flex-1 overflow-y-auto panel-scrollbar">
                                <AnimatePresence mode="wait" initial={false}>
                                    <motion.div
                                        key={activeAccountTab}
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        exit={{ opacity: 0 }}
                                        transition={{ duration: 0.1 }}
                                    >
                                        {activeAccountTab === "profile" && <ProfileTab />}
                                        {activeAccountTab === "customize" && <CustomizeTab />}
                                    </motion.div>
                                </AnimatePresence>
                            </div>

                            {/* Bottom nav */}
                            <div className="shrink-0 border-t border-white/10 bg-black/10">
                                <div className="overflow-x-auto panel-scrollbar">
                                    <div className="flex items-center justify-between px-4 py-5 md:py-4 min-w-fit">
                                <div className="flex items-center">
                                    {accountTabs.map((tab) => {
                                        const btn = (
                                            <ButtonHapticWrapper key={tab.id}><button
                                                onClick={() => setActiveAccountTab(tab.id)}
                                                className={`relative flex items-center justify-center gap-2 py-2.5 px-4 rounded-full transition-colors ${
                                                    activeAccountTab === tab.id
                                                        ? "text-white bg-white/15"
                                                        : "text-white/30 hover:text-white/50"
                                                }`}
                                            >
                                                <span>
                                                    {tabIcons[tab.id]}
                                                </span>
                                                <AnimatePresence>
                                                    {activeAccountTab === tab.id && (
                                                        <motion.span
                                                            initial={{ width: 0, opacity: 0 }}
                                                            animate={{ width: "auto", opacity: 1 }}
                                                            exit={{ width: 0, opacity: 0 }}
                                                            transition={{ duration: 0.2, ease: "easeOut" }}
                                                            className="overflow-hidden whitespace-nowrap font-changa-one text-sm"
                                                        >
                                                            {tab.label}
                                                        </motion.span>
                                                    )}
                                                </AnimatePresence>
                                            </button></ButtonHapticWrapper>
                                        );

                                        return btn;
                                    })}
                                </div>

                                <div className="flex items-center gap-1">
                                        <button
                                            onClick={handleLogout}
                                            className="p-2.5 rounded-full text-red-500 hover:text-red-400 hover:bg-red-500/10 transition-colors cursor-pointer"
                                        >
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                                <path d="M12 3V12M18.3611 5.64001C19.6195 6.8988 20.4764 8.50246 20.8234 10.2482C21.1704 11.994 20.992 13.8034 20.3107 15.4478C19.6295 17.0921 18.4759 18.4976 16.9959 19.4864C15.5159 20.4752 13.776 21.0029 11.9961 21.0029C10.2162 21.0029 8.47625 20.4752 6.99627 19.4864C5.51629 18.4976 4.36274 17.0921 3.68146 15.4478C3.00019 13.8034 2.82179 11.994 3.16882 10.2482C3.51584 8.50246 4.37272 6.8988 5.6311 5.64001" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        </button>
                                </div>
                                </div>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </motion.div>,
        document.body
    );
};

export default UserPanel;
